/**
 * ResubscribeDialog.tsx — the ADMIN on-behalf re-subscribe, both lanes.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: end-of-term re-subscribe.
 *
 * A plan that runs its full term ends, and one-month plans deliberately do not
 * auto-renew — the patient must actively choose again. Until now nobody could
 * help them do that. This is the back-office half of the answer.
 *
 * TWO LANES, AND THE DEFAULT IS THE POINT.
 *   LANE B (`patient_confirmed`) is the default and will be almost all volume:
 *     the admin starts it, the patient gets a link and confirms the new term
 *     themselves. A new term is a NEW CONTRACT — the original mandate covered
 *     the original term only — so the patient's own confirmation is what
 *     authorises the charge.
 *   LANE A (`admin_authorised`) exists because some patients genuinely cannot
 *     self-serve. The admin takes the yes on the call and RECORDS how and when.
 *
 * THE OVERRIDE MUST LOOK LIKE THE EXCEPTION IT IS. Lane B is pre-selected and
 * needs a reason and one click. Lane A is second in the list, reveals a warning,
 * and demands four more things — a method with no default so a mis-click cannot
 * pick one, when the patient actually said yes, an explicit attestation, and
 * the same required reason. It must never be the easier of the two buttons.
 *
 * SAME GRAMMAR AS A REFUND. This is the shared `ConfirmDialog` with
 * `requireReason`, exactly like the refund flow, because both are "an admin did
 * something with a patient's money and the audit row has to name them". The
 * extra lane-A gate rides on the dialog's additive `confirmDisabled`, so the
 * reason requirement is never weakened to make room for it.
 *
 * NOTHING IS CHARGED HERE. A confirm places a HOLD on the vaulted card; the
 * capture happens only when a clinician approves the NEW order case against a
 * NEW approval. A repeat supply of a prescription-only medicine gets a
 * clinician every time — the prior term's approval is not reachable from it.
 *
 * R28: category-level copy only — the treatment is never named on this screen.
 */
import { useEffect, useState } from 'react';
import { ApiError } from '../api/client';
import { initiateResubscribe } from '../api/orders';
import type { PlanMonths, ResubscribeInitiateResult, ResubscribeLane } from '../api/types';
import { formatPence } from '../lib/format';
import {
  AUTHORISATION_METHODS,
  MAX_AUTHORISATION_AGE_DAYS,
  monthsLabel,
} from '../resubscribe/resubscribeCopy';
import {
  AUTHORISATION_REFERENCE_MAX,
  EMPTY_AUTHORISATION_DRAFT,
  checkAuthorisation,
  type AuthorisationDraft,
} from '../resubscribe/authorisationGate';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { Alert, TextInput } from '../ui/kit';

/** The terms a plan can be bought on (contracts `PlanMonths`). */
const TERM_OPTIONS: PlanMonths[] = [1, 3, 6, 12];

const fieldsetStyle = {
  border: '1px solid var(--gps-border)',
  borderRadius: 'var(--gps-r-md)',
  padding: 'var(--gps-s-3)',
  margin: '0 0 var(--gps-s-3)',
} as const;

const legendStyle = {
  fontSize: '12.5px',
  fontWeight: 700,
  color: 'var(--gps-muted)',
  padding: '0 6px',
} as const;

const radioRowStyle = {
  display: 'flex',
  gap: '10px',
  alignItems: 'flex-start',
  padding: '6px 0',
  fontSize: '14px',
  lineHeight: 1.45,
  cursor: 'pointer',
} as const;

export function ResubscribeDialog({
  open,
  priorSubscriptionId,
  priorMonths,
  priorMonthlyPence,
  onDone,
  onCancel,
}: {
  open: boolean;
  priorSubscriptionId: string;
  /** The finished plan's term — the default for the new one. */
  priorMonths: PlanMonths;
  /** The finished plan's monthly price, shown for orientation only (see the WHY below). */
  priorMonthlyPence: number;
  /**
   * Called with whatever the initiate answered — the created offer on lane B,
   * the inline confirm result on lane A — so the drawer can show what happened
   * next. The union is deliberate: the two lanes do not return the same shape.
   */
  onDone: (result: ResubscribeInitiateResult) => void;
  onCancel: () => void;
}) {
  const [lane, setLane] = useState<ResubscribeLane>('patient_confirmed');
  const [months, setMonths] = useState<PlanMonths>(priorMonths);
  const [authorisation, setAuthorisation] = useState<AuthorisationDraft>(EMPTY_AUTHORISATION_DRAFT);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A re-opened dialog must never carry the previous attempt's lane, term or —
  // above all — a previous patient's recorded authorisation. Same discipline as
  // ConfirmDialog's own reason reset.
  useEffect(() => {
    if (!open) return;
    setLane('patient_confirmed');
    setMonths(priorMonths);
    setAuthorisation(EMPTY_AUTHORISATION_DRAFT);
    setError(null);
  }, [open, priorMonths]);

  const isOverride = lane === 'admin_authorised';
  const check = checkAuthorisation(authorisation);
  // Lane B has nothing extra to satisfy; lane A must pass every condition. The
  // reason gate is ConfirmDialog's own and is ANDed with this, never replaced.
  const gateBlocked = isOverride && !check.ready;

  function submit(reason?: string) {
    // `requireReason` guarantees a reason; the guard is here so a future edit
    // that drops the flag fails loudly rather than posting a body the server
    // rejects with a 400 the admin cannot read.
    if (!reason) return;
    if (isOverride && !check.value) return;
    setBusy(true);
    setError(null);
    initiateResubscribe({
      priorSubscriptionId,
      lane,
      months,
      reason,
      ...(isOverride && check.value ? { authorisation: check.value } : {}),
    })
      .then((offer) => {
        setBusy(false);
        onDone(offer);
      })
      .catch((err: unknown) => {
        setBusy(false);
        // 409 is the server's own eligibility re-check winning — someone else
        // started an offer, or the plan stopped qualifying while this dialog was
        // open. Say so plainly; the drawer reload will show the new truth.
        const conflict = err instanceof ApiError && err.status === 409;
        setError(
          conflict
            ? 'This plan can no longer be re-subscribed — someone may have started an offer already. Close and reopen this plan to see where it stands.'
            : 'Sorry — we could not start the re-subscribe. Please try again.',
        );
      });
  }

  return (
    <ConfirmDialog
      open={open}
      title="Start a new plan for this patient"
      confirmLabel={isOverride ? 'Record authorisation and start' : 'Send the patient a confirmation'}
      busy={busy}
      requireReason
      confirmDisabled={gateBlocked}
      reasonLabel="Why are you starting this?"
      onConfirm={submit}
      onCancel={() => {
        if (!busy) onCancel();
      }}
      body={
        <>
          {error ? (
            <Alert tone="danger" role="alert" style={{ marginBottom: 'var(--gps-s-3)' }}>
              {error}
            </Alert>
          ) : null}

          <p style={{ margin: '0 0 var(--gps-s-3)' }}>
            Their last plan was {monthsLabel(priorMonths)} at {formatPence(priorMonthlyPence)} a month. The new
            term is priced at today’s rate, so the amount may differ — the patient sees the exact figure before
            anything is authorised.
          </p>

          <fieldset style={fieldsetStyle}>
            <legend style={legendStyle}>How is this being authorised?</legend>
            {/* Lane B FIRST and pre-selected. The order is not cosmetic: the
                patient confirming is the default the CTO settled on, and the
                override should take deliberate effort to reach. */}
            <label style={radioRowStyle}>
              <input
                type="radio"
                name="resubscribe-lane"
                value="patient_confirmed"
                checked={!isOverride}
                onChange={() => setLane('patient_confirmed')}
                disabled={busy}
                style={{ marginTop: '3px', flex: 'none' }}
              />
              <span>
                <strong>The patient confirms</strong> — we email them a link and they agree to the new term
                themselves. This is the normal route.
              </span>
            </label>
            <label style={radioRowStyle}>
              <input
                type="radio"
                name="resubscribe-lane"
                value="admin_authorised"
                checked={isOverride}
                onChange={() => setLane('admin_authorised')}
                disabled={busy}
                style={{ marginTop: '3px', flex: 'none' }}
              />
              <span>
                <strong>I have the patient’s authorisation</strong> — only for a patient who cannot confirm it
                themselves.
              </span>
            </label>
          </fieldset>

          <fieldset style={fieldsetStyle}>
            <legend style={legendStyle}>New term</legend>
            {/* A different term is a commercial choice, not a different clinical
                decision — "she wants 3 months this time" is the common ask, so
                it is editable here. The medicine is NOT: a different treatment
                is a new clinical plan and goes down the pre-approve lane. */}
            <label htmlFor="resubscribe-months" style={{ display: 'block', fontSize: '12.5px', fontWeight: 600, color: 'var(--gps-muted)', marginBottom: '6px' }}>
              Plan term
            </label>
            <select
              id="resubscribe-months"
              value={months}
              disabled={busy}
              onChange={(e) => setMonths(Number(e.target.value) as PlanMonths)}
              style={{ width: '100%', minHeight: '44px', padding: '10px 12px', font: 'inherit', fontSize: '15px', borderRadius: 'var(--gps-r-md)', border: '1.5px solid var(--gps-border)', background: 'var(--gps-surface)', color: 'var(--gps-text)' }}
            >
              {TERM_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {monthsLabel(option)}
                </option>
              ))}
            </select>
          </fieldset>

          {isOverride ? (
            <AuthorisationFieldset
              draft={authorisation}
              takenAtError={check.takenAtError}
              busy={busy}
              onChange={setAuthorisation}
            />
          ) : null}

          <p style={{ margin: '0 0 var(--gps-s-2)', fontSize: '13.5px', color: 'var(--gps-muted)' }}>
            Nothing is charged now. A clinician has to approve the new order before any payment is taken, exactly
            as they did the first time.
          </p>
        </>
      }
    />
  );
}

/**
 * The lane-A evidence block. Every field here exists to answer a single
 * question an auditor will ask: who authorised what, how, and when.
 *
 * The method radios have NO pre-selected value on purpose — a default would let
 * a mis-click record "phone call" against a conversation that never happened,
 * and this row is the only evidence the conversation happened at all.
 */
function AuthorisationFieldset({
  draft,
  takenAtError,
  busy,
  onChange,
}: {
  draft: AuthorisationDraft;
  takenAtError: string | null;
  busy: boolean;
  onChange: (next: AuthorisationDraft) => void;
}) {
  return (
    <fieldset style={{ ...fieldsetStyle, borderColor: 'var(--gps-warn-text)' }}>
      <legend style={legendStyle}>Record the authorisation</legend>

      <Alert tone="warning" role="status" style={{ marginBottom: 'var(--gps-s-3)' }}>
        You are authorising a charge on the patient’s behalf. What you record here is the only evidence they
        agreed, and it is kept against your name.
      </Alert>

      <fieldset style={{ border: 0, padding: 0, margin: '0 0 var(--gps-s-3)' }}>
        <legend style={{ ...legendStyle, padding: 0, marginBottom: '4px' }}>How did they authorise it?</legend>
        {AUTHORISATION_METHODS.map((method) => (
          <label key={method.value} style={radioRowStyle}>
            <input
              type="radio"
              name="resubscribe-authorisation-method"
              value={method.value}
              checked={draft.method === method.value}
              onChange={() => onChange({ ...draft, method: method.value })}
              disabled={busy}
              style={{ marginTop: '3px', flex: 'none' }}
            />
            <span>{method.label}</span>
          </label>
        ))}
      </fieldset>

      <div style={{ marginBottom: 'var(--gps-s-3)' }}>
        <label htmlFor="resubscribe-taken-at" style={{ display: 'block', fontSize: '12.5px', fontWeight: 600, color: 'var(--gps-muted)', marginBottom: '6px' }}>
          When did they authorise it?
        </label>
        <TextInput
          id="resubscribe-taken-at"
          type="datetime-local"
          value={draft.takenAtLocal}
          disabled={busy}
          onChange={(e) => onChange({ ...draft, takenAtLocal: e.target.value })}
          aria-describedby="resubscribe-taken-at-note"
          aria-invalid={takenAtError ? true : undefined}
        />
        <div
          id="resubscribe-taken-at-note"
          role={takenAtError ? 'alert' : undefined}
          style={{ fontSize: '12px', marginTop: '4px', color: takenAtError ? 'var(--gps-danger-text)' : 'var(--gps-muted)' }}
        >
          {takenAtError ?? `The time on your own clock. Must be within the last ${MAX_AUTHORISATION_AGE_DAYS} days.`}
        </div>
      </div>

      <div style={{ marginBottom: 'var(--gps-s-3)' }}>
        <label htmlFor="resubscribe-authorisation-ref" style={{ display: 'block', fontSize: '12.5px', fontWeight: 600, color: 'var(--gps-muted)', marginBottom: '6px' }}>
          Call or ticket reference (optional)
        </label>
        {/* Optional because we cannot guarantee a CRM ticket exists — but the
            audit row records WHETHER one was given, so an auditor can see at a
            glance which of these rows have corroboration. */}
        <TextInput
          id="resubscribe-authorisation-ref"
          value={draft.reference}
          maxLength={AUTHORISATION_REFERENCE_MAX}
          disabled={busy}
          onChange={(e) => onChange({ ...draft, reference: e.target.value })}
        />
      </div>

      {/* Starts unchecked, always. The wire type is `attested: true`, so an
          unattested request is not merely rejected — it cannot be expressed. */}
      <label style={radioRowStyle}>
        <input
          type="checkbox"
          checked={draft.attested}
          onChange={(e) => onChange({ ...draft, attested: e.target.checked })}
          disabled={busy}
          style={{ width: '18px', height: '18px', marginTop: '2px', flex: 'none', accentColor: 'var(--gps-primary)' }}
        />
        <span>
          I confirm the patient authorised this charge on the date and by the method recorded above.
        </span>
      </label>
    </fieldset>
  );
}
